import { Injectable } from '@angular/core';
import { ERoles } from '../_models/entities/roles';
import { TabDef } from '../_models/components/tab-def';

export interface ModulePermissions {
  [key: string]: ERoles[];
}

export interface ContextMenuPermissions {
  [key: string]: {
    [action: string]: ERoles[];
  };
}

export interface TabPermissions {
  [key: string]: {
    [tabKey: string]: ERoles[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class RolePermissionsService {
  // Define module access permissions
  private modulePermissions: ModulePermissions = {
    home: [
      ERoles.superadmin,
      ERoles.operationsadmin,
      ERoles.operationsmanager,
      ERoles.salesmanager,
      ERoles.salesagent,
      // ERoles.ispadmin,
      ERoles.vnoadmin,
      ERoles.pmoadmin
    ],
    operations: [
      ERoles.superadmin,
      ERoles.operationsmanager,
      ERoles.operationsadmin,
      ERoles.ispadmin,
      ERoles.ispoperator,
      ERoles.ispviewer
    ],
    services: [ERoles.superadmin, ERoles.ispadmin],
    support: [ERoles.superadmin],
    projects: [ERoles.superadmin, ERoles.pmoadmin],
    'system-management': [ERoles.superadmin],
    'portal-management': [
      ERoles.superadmin,
      // ERoles.operationsadmin,
      ERoles.portalmanagement_admin,
      ERoles.portalmanagement_user
    ],
    settings: [ERoles.superadmin]
  };

  // Define context menu action permissions
  private contextMenuPermissions: ContextMenuPermissions = {
    operations: {
      add: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator],
      edit: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator],
      delete: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator],
      view: [
        ERoles.superadmin,
        ERoles.operationsmanager,
        ERoles.operationsadmin,
        ERoles.ispadmin,
        ERoles.ispoperator,
        ERoles.ispviewer
      ],
      configure: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator],
      activate: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator],
      deactivate: [
        ERoles.superadmin,
        ERoles.operationsmanager,
        ERoles.ispadmin,
        ERoles.ispoperator
      ],
      provision: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator],
      remove: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator],
      manage: [ERoles.superadmin, ERoles.operationsmanager, ERoles.ispadmin, ERoles.ispoperator]
    },
    services: {
      add: [ERoles.superadmin],
      edit: [ERoles.superadmin],
      delete: [ERoles.superadmin],
      view: [ERoles.superadmin],
      manage: [ERoles.superadmin, ERoles.ispadmin]
    },
    'portal-management': {
      add: [ERoles.superadmin, ERoles.portalmanagement_admin],
      edit: [ERoles.superadmin, ERoles.portalmanagement_admin],
      delete: [ERoles.superadmin],
      view: [ERoles.superadmin, ERoles.portalmanagement_admin, ERoles.portalmanagement_user],
      override: [ERoles.superadmin, ERoles.operationsadmin],
      'reset-password': [ERoles.superadmin, ERoles.operationsadmin, ERoles.portalmanagement_admin]
    },
    'system-management': {
      add: [ERoles.superadmin],
      edit: [ERoles.superadmin],
      delete: [ERoles.superadmin],
      view: [ERoles.superadmin],
      configure: [ERoles.superadmin],
      'reset-password': [ERoles.superadmin]
    },
    projects: {
      add: [ERoles.superadmin, ERoles.pmoadmin],
      edit: [ERoles.superadmin, ERoles.pmoadmin],
      delete: [ERoles.superadmin, ERoles.pmoadmin],
      view: [ERoles.superadmin, ERoles.pmoadmin],
      manage: [ERoles.superadmin, ERoles.pmoadmin]
    }
  };

  // Define tab-level permissions for each module
  private tabPermissions: TabPermissions = {
    operations: {
      'overview-dashboard': [
        ERoles.superadmin,
        ERoles.operationsmanager,
        ERoles.operationsadmin,
        ERoles.ispadmin,
        ERoles.ispoperator,
        ERoles.ispviewer
      ],
      management: [ERoles.superadmin, ERoles.operationsmanager],
      'orders-feasibility': [
        ERoles.superadmin,
        ERoles.operationsmanager,
        ERoles.operationsadmin,
        ERoles.ispadmin,
        ERoles.ispoperator,
        ERoles.ispviewer
      ],
      'customer-circuits': [
        ERoles.superadmin,
        ERoles.operationsmanager,
        ERoles.operationsadmin,
        ERoles.ispadmin,
        ERoles.ispoperator,
        ERoles.ispviewer
      ],
      'device-monitoring': [ERoles.superadmin],
      'network-health': [ERoles.superadmin, ERoles.operationsmanager],
      'stock-levels': [ERoles.superadmin],
      'workforce-installs': [ERoles.superadmin]
    },
    services: {
      'overview-dashboard': [ERoles.superadmin, ERoles.ispadmin],
      'products-promotions': [ERoles.superadmin, ERoles.ispadmin],
      accounts: [ERoles.superadmin, ERoles.ispadmin],
      management: [ERoles.superadmin, ERoles.ispadmin]
    },
    'portal-management': {
      customers: [
        ERoles.superadmin,
        ERoles.operationsadmin,
        ERoles.portalmanagement_admin,
        ERoles.portalmanagement_user
      ],
      subscriptions: [
        ERoles.superadmin,
        ERoles.operationsadmin,
        ERoles.portalmanagement_admin,
        ERoles.portalmanagement_user
      ],
      transactions: [ERoles.superadmin, ERoles.operationsadmin, ERoles.portalmanagement_admin],
      packages: [ERoles.superadmin, ERoles.operationsadmin, ERoles.portalmanagement_admin],
      settings: [ERoles.superadmin, ERoles.operationsadmin, ERoles.portalmanagement_admin]
    },
    'system-management': {
      users: [ERoles.superadmin],
      tenants: [ERoles.superadmin],
      'audit-logs': [ERoles.superadmin],
      'task-executions': [ERoles.superadmin],
      'system-settings': [ERoles.superadmin]
    },
    projects: {
      overview: [ERoles.superadmin, ERoles.pmoadmin],
      'active-projects': [ERoles.superadmin, ERoles.pmoadmin],
      'completed-projects': [ERoles.superadmin, ERoles.pmoadmin],
      dashboard: [ERoles.superadmin, ERoles.pmoadmin],
      statistics: [ERoles.superadmin, ERoles.pmoadmin],
      management: [ERoles.superadmin, ERoles.pmoadmin],
      stock: [ERoles.superadmin, ERoles.pmoadmin],
      reports: [ERoles.superadmin, ERoles.pmoadmin],
      'customer-portal': [ERoles.superadmin, ERoles.pmoadmin],
      settings: [ERoles.superadmin, ERoles.pmoadmin]
    }
  };

  constructor() {}

  /**
   * Check if user has access to a specific module
   * @param moduleName - The name of the module
   * @param userRole - The user's role
   * @returns boolean indicating if user has access
   */
  hasModuleAccess(moduleName: string, userRole: string): boolean {
    const allowedRoles = this.modulePermissions[moduleName];
    if (!allowedRoles) {
      console.warn(`No permissions defined for module: ${moduleName}`);
      return false;
    }
    return allowedRoles.includes(userRole as ERoles);
  }

  /**
   * Check if user has permission for a specific context menu action
   * @param moduleName - The name of the module
   * @param action - The action name (e.g., 'add', 'edit', 'delete')
   * @param userRole - The user's role
   * @returns boolean indicating if user has permission
   */
  hasContextMenuPermission(moduleName: string, action: string, userRole: string): boolean {
    const modulePermissions = this.contextMenuPermissions[moduleName];
    if (!modulePermissions) {
      console.warn(`No context menu permissions defined for module: ${moduleName}`);
      return false;
    }

    const actionPermissions = modulePermissions[action];
    if (!actionPermissions) {
      console.warn(`No permissions defined for action: ${action} in module: ${moduleName}`);
      return false;
    }

    return actionPermissions.includes(userRole as ERoles);
  }

  /**
   * Get all allowed roles for a specific module
   * @param moduleName - The name of the module
   * @returns array of allowed roles
   */
  getModuleAllowedRoles(moduleName: string): ERoles[] {
    return this.modulePermissions[moduleName] || [];
  }

  /**
   * Get all allowed roles for a specific context menu action
   * @param moduleName - The name of the module
   * @param action - The action name
   * @returns array of allowed roles
   */
  getContextMenuAllowedRoles(moduleName: string, action: string): ERoles[] {
    const modulePermissions = this.contextMenuPermissions[moduleName];
    if (!modulePermissions) return [];

    return modulePermissions[action] || [];
  }

  /**
   * Check if user has access to a specific tab within a module
   * @param moduleName - The name of the module
   * @param tabKey - The tab key
   * @param userRole - The user's role
   * @returns boolean indicating if user has access
   */
  hasTabAccess(moduleName: string, tabKey: string, userRole: string): boolean {
    const moduleTabPermissions = this.tabPermissions[moduleName];
    if (!moduleTabPermissions) {
      console.warn(`No tab permissions defined for module: ${moduleName}`);
      return false;
    }

    const allowedRoles = moduleTabPermissions[tabKey];
    if (!allowedRoles) {
      console.warn(`No permissions defined for tab: ${tabKey} in module: ${moduleName}`);
      return false;
    }

    return allowedRoles.includes(userRole as ERoles);
  }

  /**
   * Get all allowed roles for a specific tab
   * @param moduleName - The name of the module
   * @param tabKey - The tab key
   * @returns array of allowed roles
   */
  getTabAllowedRoles(moduleName: string, tabKey: string): ERoles[] {
    const moduleTabPermissions = this.tabPermissions[moduleName];
    if (!moduleTabPermissions) return [];

    return moduleTabPermissions[tabKey] || [];
  }

  /**
   * Filter tabs based on user permissions
   * @param moduleName - The name of the module
   * @param tabs - Array of tab objects with key and name properties
   * @param userRole - The user's role
   * @returns filtered array of tabs
   */
  filterTabs(moduleName: string, tabs: TabDef[], userRole: string): TabDef[] {
    return tabs.filter((tab) => {
      return this.hasTabAccess(moduleName, tab.key, userRole);
    });
  }

  /**
   * Filter context menu items based on user permissions
   * @param moduleName - The name of the module
   * @param menuItems - Array of menu items to filter
   * @param userRole - The user's role
   * @returns filtered array of menu items
   */
  filterContextMenuItems(moduleName: string, menuItems: any[], userRole: string): any[] {
    return menuItems.filter((item) => {
      // If item has a permission property, check it
      if (item.permission) {
        return this.hasContextMenuPermission(moduleName, item.permission, userRole);
      }

      // If item has a label that matches common actions, check permissions
      const action = this.getActionFromLabel(item.label);
      if (action) {
        return this.hasContextMenuPermission(moduleName, action, userRole);
      }

      // If no permission check is possible, allow by default
      return true;
    });
  }

  /**
   * Extract action from menu item label
   * @param label - The menu item label
   * @returns action name or null
   */
  private getActionFromLabel(label: string): string | null {
    const labelLower = label.toLowerCase();

    if (labelLower.includes('add') || labelLower.includes('create') || labelLower.includes('new')) {
      return 'add';
    }
    if (
      labelLower.includes('edit') ||
      labelLower.includes('update') ||
      labelLower.includes('modify')
    ) {
      return 'edit';
    }
    if (
      labelLower.includes('delete') ||
      (labelLower.includes('remove') && !labelLower.includes('provision'))
    ) {
      return 'delete';
    }
    if (
      labelLower.includes('view') ||
      labelLower.includes('show') ||
      labelLower.includes('details')
    ) {
      return 'view';
    }
    if (labelLower.includes('configure') || labelLower.includes('settings')) {
      return 'configure';
    }
    if (labelLower.includes('manage')) {
      return 'manage';
    }
    if (labelLower.includes('override')) {
      return 'override';
    }
    if (labelLower.includes('reset') || labelLower.includes('password')) {
      return 'reset-password';
    }
    if (labelLower.includes('activate') || labelLower.includes('activation')) {
      return 'activate';
    }
    if (labelLower.includes('deactivate') || labelLower.includes('deactivation')) {
      return 'deactivate';
    }
    if (labelLower.includes('provision') || labelLower.includes('provisioning')) {
      return 'provision';
    }

    return null;
  }

  /**
   * Add permission property to menu items for easier filtering
   * @param menuItems - Array of menu items
   * @param moduleName - The name of the module
   * @returns menu items with permission properties
   */
  addPermissionProperties(menuItems: any[], moduleName: string): any[] {
    return menuItems.map((item) => {
      const action = this.getActionFromLabel(item.label);
      if (action) {
        return { ...item, permission: action };
      }
      return item;
    });
  }
}
